import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';
import { openStepsOnBehalf } from './reviewStage';
import { RISK_OPTIONS } from './constants';
import type { RiskLevel } from './types';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

interface Props {
  cycle: ReviewCycle;
  isDecisionMaker: boolean;
  userEmail?: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onCompleted: () => void;
}

/**
 * What the sequence is still waiting on, and the decision maker's way past it —
 * 2026-08-31 (Fredrik, from the DM view on a live cycle).
 *
 * Approval is now refused while any step is open (STEPS_NOT_COMPLETE). That
 * gate is only defensible if the DM has a way to advance a cycle whose reviewer
 * has left or is unreachable, and the backend has always allowed exactly that:
 * ASSESSED_STEPS exempts `content_review.decision_maker` for medical and legal,
 * READBACK_REQUIRED exempts it for producer. Neither exemption had a control.
 *
 * Named, not anonymous. The producer-step version of this shipped hours earlier
 * after an admin was shown a green button identical to the producer's own,
 * completing her step with nothing saying so; this one starts from that lesson.
 * It states whose step it is, what will NOT be recorded, and confirms with both
 * in the dialog.
 */
export default function OpenStepPanel({
  cycle, isDecisionMaker, userEmail, getAuthHeaders, onCompleted
}: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which step, if any, the decision maker is reviewing themselves. The step is
  // CHOSEN by which row you open — an assessment that records only its author
  // cannot say which of several steps it stands for, so the choice has to be an
  // act rather than an inference.
  const [reviewingStep, setReviewingStep] = useState<string | null>(null);
  const [riskLevel, setRiskLevel] = useState<RiskLevel | ''>('');
  const [summary, setSummary] = useState('');

  const open = openStepsOnBehalf(cycle, isDecisionMaker, userEmail);
  if (open.length === 0) return null;

  async function complete(step: string, owner: string, missing: string) {
    if (!window.confirm(
      `Complete the ${step} step on behalf of ${owner}?\n\n` +
      `You are standing in because they are unavailable. ${missing.charAt(0).toUpperCase()}${missing.slice(1)} ` +
      'will NOT be recorded, so you will decide with one fewer input than the sequence intended. ' +
      'If they can still do it themselves, ask them instead.'
    )) return;
    setBusy(step); setError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/steps/${step}/complete`,
        {},
        { headers }
      );
      onCompleted();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to complete the ${step} step`);
    } finally {
      setBusy(null);
    }
  }

  /** Do the review yourself: file the assessment FOR THAT STEP, then complete
   *  it — the same coupling "Complete Review" uses for a reviewer, so a filed
   *  assessment never sits behind a step nobody handed over. */
  async function reviewMyself(step: string, owner: string) {
    if (!riskLevel) { setError('Set a risk level before submitting.'); return; }
    if (!window.confirm(
      `Submit your own ${step} review, standing in for ${owner}?\n\n` +
      `It is recorded as YOUR assessment, marked as standing in for the ${step} step, ` +
      'and the step is then complete. The substitution stays visible on the record.'
    )) return;
    setBusy(step); setError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/assessments`,
        { riskLevel, summary: summary.trim(), standsInFor: { step } },
        { headers }
      );
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/steps/${step}/complete`,
        {},
        { headers }
      );
      setReviewingStep(null); setRiskLevel(''); setSummary('');
      onCompleted();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to submit the ${step} review`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="review-section" data-testid="open-step-panel">
      <h3>Waiting on the review sequence</h3>
      <div className="viewer-tab-hint">
        This cycle cannot be <strong>approved</strong> until every review step is complete &mdash;
        the assessments are what the decision rests on. Rejecting it or requesting a revision is
        unaffected: sending work back does not wait on a review of something that is going back.
      </div>

      {error && (
        <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, margin: '12px 0', fontSize: 14 }}>
          {error}
        </div>
      )}

      {open.map(o => (
        <div
          key={o.step}
          data-testid={`open-step-${o.step}`}
          style={{ padding: '12px 14px', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 8, marginTop: 12, fontSize: 13, color: '#664d03' }}
        >
          <strong>Waiting on {o.owner}.</strong>
          <p style={{ margin: '4px 0 10px' }}>
            If they are unavailable you can complete this step for them &mdash; but {o.missing} will
            not be recorded.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="review-btn review-btn-outline"
              onClick={() => complete(o.step, o.owner, o.missing)}
              disabled={busy !== null}
              style={{ fontSize: 13, padding: '6px 14px' }}
              title={`Completes the ${o.step} step without ${o.missing}.`}
            >
              {busy === o.step ? 'Completing...' : `Complete the ${o.step} step on their behalf`}
            </button>
            {/* The producer step has no risk assessment to file — its input is
                a commercial read-back, and only the producer can give that. */}
            {o.step !== 'producer' && reviewingStep !== o.step && (
              <button
                className="review-btn review-btn-outline"
                onClick={() => { setReviewingStep(o.step); setError(null); }}
                disabled={busy !== null}
                data-testid={`review-myself-${o.step}`}
                style={{ fontSize: 13, padding: '6px 14px' }}
                title={`File your own ${o.step} assessment, recorded as standing in for ${o.owner}.`}
              >
                Do the {o.step} review myself
              </button>
            )}
          </div>

          {reviewingStep === o.step && (
            <div data-testid={`review-myself-form-${o.step}`} style={{ marginTop: 12 }}>
              <p style={{ margin: '0 0 8px' }}>
                Recorded as <strong>your</strong> assessment, marked as standing in for
                the {o.step} step. It is not filed as {o.owner}&rsquo;s.
              </p>
              <div className="review-form-group" style={{ maxWidth: 240 }}>
                <label htmlFor={`stand-in-risk-${o.step}`}>Risk level</label>
                <select
                  id={`stand-in-risk-${o.step}`}
                  value={riskLevel}
                  onChange={e => setRiskLevel(e.target.value as RiskLevel)}
                >
                  <option value="">Select...</option>
                  {RISK_OPTIONS.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
              </div>
              <div className="review-form-group">
                <label htmlFor={`stand-in-summary-${o.step}`}>Summary (optional)</label>
                <textarea
                  id={`stand-in-summary-${o.step}`}
                  value={summary}
                  onChange={e => setSummary(e.target.value)}
                  placeholder={`Your ${o.step} assessment of this content...`}
                  rows={2}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="review-btn review-btn-success"
                  onClick={() => reviewMyself(o.step, o.owner)}
                  disabled={!riskLevel || busy !== null}
                  style={{ fontSize: 13, padding: '6px 14px' }}
                >
                  {busy === o.step ? 'Submitting...' : `Submit and complete the ${o.step} step`}
                </button>
                <button
                  className="review-btn review-btn-outline"
                  onClick={() => { setReviewingStep(null); setError(null); }}
                  disabled={busy !== null}
                  style={{ fontSize: 13, padding: '6px 14px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
